import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { User, ClientSubscription, SubscriptionPlan } from '../../types';
import BkashPaymentModal from '../../components/payment/BkashPaymentModal';

type ClientWithSubscription = User & { subscription?: ClientSubscription; planName?: string };

interface UpgradePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: ClientWithSubscription | null;
  onSuccess: () => void;
}

const UpgradePlanModal: React.FC<UpgradePlanModalProps> = ({ isOpen, onClose, client, onSuccess }) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
      setSelectedPlan(null);
    }
  }, [isOpen]);

  const loadPlans = async () => {
    setLoading(true);
    const response = await api.getResellerPlans();
    if (response.success && response.data) {
      setPlans(response.data.filter(p => p.isActive));
    }
    setLoading(false);
  };

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setShowPayment(true);
  };

  const handlePaymentSuccess = (_result: { paymentId: string; trxId: string; subscriptionId: string; user?: any }) => {
    setShowPayment(false);
    onSuccess();
    onClose();
  };

  if (!isOpen || !client) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
        <div className="bg-[#121212] border border-[#262626] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[#262626]">
            <h3 className="text-lg font-bold text-white">Upgrade Client Subscription</h3>
            <p className="text-neutral-500 text-sm">Select a new plan for {client.email}</p>
            {client.planName && (
              <p className="text-emerald-400 text-sm mt-1">Current plan: {client.planName}</p>
            )}
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      selectedPlan?.id === plan.id
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-[#262626] hover:border-[#363636] bg-[#1a1a1a]'
                    }`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-white font-bold">{plan.name}</h4>
                          {plan.isFeatured && (
                            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded">Popular</span>
                          )}
                        </div>
                        <p className="text-neutral-500 text-sm mt-1">{plan.description}</p>
                        <p className="text-neutral-400 text-xs mt-2">
                          {plan.maxRequestsPerDay} daily / {plan.maxRequestsPerMonth.toLocaleString()} monthly requests
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-white">৳{plan.priceBdt.toLocaleString()}</p>
                        <p className="text-neutral-500 text-sm">/{plan.durationDays} days</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-6 border-t border-[#262626] flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-[#1a1a1a] hover:bg-[#262626] text-white rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => selectedPlan && handleSelectPlan(selectedPlan)}
              disabled={!selectedPlan}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
            >
              Proceed to Payment
            </button>
          </div>
        </div>
      </div>

      {showPayment && selectedPlan && client && (
        <BkashPaymentModal
          isOpen={showPayment}
          onClose={() => setShowPayment(false)}
          plan={selectedPlan}
          clientData={{
            email: client.email,
            firstName: client.firstName,
            lastName: client.lastName,
            phone: client.phone,
            companyName: client.companyName,
          }}
          onPaymentComplete={handlePaymentSuccess}
        />
      )}
    </>
  );
};

const ResellerClients: React.FC = () => {
  const [clients, setClients] = useState<ClientWithSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedClient, setSelectedClient] = useState<ClientWithSubscription | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    loadClients();
  }, [page, statusFilter]);

  const loadClients = async () => {
    setLoading(true);
    const response = await api.getResellerClients({
      page,
      pageSize: 10,
      search: search || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
    });
    if (response.success && response.data) {
      setClients(response.data.data);
      setTotalPages(response.data.totalPages);
    } else {
      setError(response.error || 'Failed to load clients');
    }
    setLoading(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadClients();
  };

  const handleDeactivate = async (clientId: string) => {
    if (!confirm('Are you sure you want to deactivate this client?')) return;

    const response = await api.deactivateClient(clientId);
    if (response.success) {
      loadClients();
    } else {
      alert(response.error || 'Failed to deactivate client');
    }
  };

  const handleUpgrade = (client: ClientWithSubscription) => {
    setSelectedClient(client);
    setShowUpgradeModal(true);
  };

  if (loading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-neutral-500 text-sm">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">My Clients</h1>
          <p className="text-neutral-500">Manage your registered clients</p>
        </div>
        <Link
          to="/reseller/create-client"
          className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-colors font-medium flex items-center gap-2"
        >
          <span>+</span>
          Add New Client
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or company name..."
              className="w-full bg-[#121212] border border-[#262626] rounded-xl px-4 py-3 pl-10 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">🔍</span>
          </div>
        </form>
        <div className="flex gap-2">
          {['all', 'active', 'pending', 'suspended'].map((status) => (
            <button
              key={status}
              onClick={() => { setStatusFilter(status); setPage(1); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                statusFilter === status
                  ? 'bg-emerald-600 text-white'
                  : 'bg-[#1a1a1a] border border-[#262626] text-neutral-400 hover:bg-[#262626] hover:text-white'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Clients Table */}
      <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
        {clients.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1a1a1a]">
                  <tr>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Client</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Plan</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Status</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Expires</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Created</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#262626]">
                  {clients.map((client) => (
                    <tr key={client.id} className="hover:bg-[#1a1a1a] transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                            <span className="text-emerald-400 font-bold">
                              {client.companyName?.charAt(0) || client.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{client.companyName || 'No Company'}</p>
                            <p className="text-neutral-500 text-sm">{client.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-white">{client.planName || 'N/A'}</p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                          client.status === 'active'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : client.status === 'pending'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : client.status === 'suspended'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-neutral-500/20 text-neutral-400'
                        }`}>
                          {client.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-neutral-400 text-sm">
                          {client.subscription?.expiresAt
                            ? new Date(client.subscription.expiresAt).toLocaleDateString('en-BD')
                            : 'N/A'}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-neutral-500 text-sm">
                          {new Date(client.createdAt).toLocaleDateString('en-BD')}
                        </p>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {client.status === 'active' && (
                            <>
                              <button
                                onClick={() => handleUpgrade(client)}
                                className="px-3 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded-lg text-xs hover:bg-blue-500/20 transition-colors"
                              >
                                Change Plan
                              </button>
                              <button
                                onClick={() => handleDeactivate(client.id)}
                                className="px-3 py-1 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-xs hover:bg-red-500/20 transition-colors"
                              >
                                Deactivate
                              </button>
                            </>
                          )}
                          {client.status === 'suspended' && (
                            <button
                              onClick={() => handleUpgrade(client)}
                              className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs hover:bg-emerald-500/20 transition-colors"
                            >
                              Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-[#262626] flex items-center justify-between">
                <p className="text-neutral-500 text-sm">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-[#1a1a1a] border border-[#262626] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#262626] transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-[#1a1a1a] border border-[#262626] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#262626] transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-[#1a1a1a] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">👥</span>
            </div>
            <p className="text-neutral-500">No clients found</p>
            <Link to="/reseller/create-client" className="text-emerald-500 text-sm hover:text-emerald-400">
              Add your first client →
            </Link>
          </div>
        )}
      </div>

      {/* Upgrade Plan Modal */}
      <UpgradePlanModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        onSuccess={loadClients}
      />
    </div>
  );
};

export default ResellerClients;
